import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function wobbleHorizontal(translateX: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateX = translateX ? translateX : 6;

	//1 - make transition steps by number of transition state steps
	const difference 	: number = Util.Number.roundDouble(translateX / 6); 						// divide the translateX value by total number of transition states
	const translateX1 	: number = Util.Number.roundDouble(translateX);
	const translateX2 	: number = Util.Number.roundDouble(translateX1 - difference);
	const translateX3 	: number = Util.Number.roundDouble(translateX2 - difference);
	const translateX4 	: number = Util.Number.roundDouble(translateX3 - difference);
	const translateX5 	: number = Util.Number.roundDouble(translateX4 - difference);
	const translateX6 	: number = Util.Number.roundDouble(translateX5 - difference);
	const translateX7 	: number = Util.Number.roundDouble(translateX6 - difference);

	//2 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'translateX(0)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX1)}px)`,
				easing 		: 'ease',
				offset 		: 0.083
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX2)}px)`,
				easing 		: 'ease',
				offset 		: 0.165
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX3)}px)`,
				easing 		: 'ease',
				offset 		: 0.330
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX4)}px)`,
				easing 		: 'ease',
				offset 		: 0.495
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX5)}px)`,
				easing 		: 'ease',
				offset 		: 0.660
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX6)}px)`,
				easing 		: 'ease',
				offset 		: 0.825
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX7)}px)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Wobble Horizontal on Event
 */
@Directive({
    selector: '[ngWobbleHorizontalOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleHorizontalOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX value is relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 1) throw new Error(`WobbleHorizontalOnEventDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.effectOptions?.translateX}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleHorizontal(this.effectOptions?.translateX),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * wobble Horizontal on Mouse Hover
 */
@Directive({
    selector: '[ngWobbleHorizontalOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleHorizontalOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef			  : ElementRef,
		private timedAnimationService : NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX value is relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 1) throw new Error(`WobbleHorizontalOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.effectOptions?.translateX}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleHorizontal(this.effectOptions?.translateX),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout 	: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomAnimationEffectOptions extends INgAnimationEffectOptions {
	translateX : number;
}
